import { inject, injectable } from '@wix/typesafe-inversify';
import type { ComponentRef, EditorSDK } from '@wix/platform-editor-sdk';
import { EditorType } from '@wix/platform-editor-sdk';
import type { IAddWidgetService } from './IAddWidgetService';
import type {
  InstallationSettings,
  InstallationSettingsById,
} from '../../utils/installationSettingsUtils';
import {
  getInstallationSettings,
  getInstallationSettingsByWidgetId,
  getMobilePresetFromEditorPresence,
} from '../../utils/installationSettingsUtils';
import { InstallPage } from '@wix/ambassador-devcenter-v1-component/types';
import { DEFAULT_WIDGET_HEIGHT, DEFAULT_WIDGET_WIDTH } from '../../constants';
import { findFirstChild } from '../../utils/findFirstChild';
import type { Preset, WidgetDescriptor } from '@wix/document-services-types';
import _ from 'lodash';
import {
  createAppWidgetInitialDefinition,
  getUniqueControllerType,
} from '../StructureBuilder/structureUtil';
import {
  buildTypeToDevCenterIdMap,
  createControllerData,
} from '../../utils/editorAppUtils';
import type {
  AddWidgetClosedOptions,
  AddWidgetOptions,
} from '../../types/addWidgetTypes';
import { removeHash } from '../../utils/removeHash';
import type { ExtendedBlocksAppData } from '../../types/ExtendedBlocksAppData';
import type { IAppDescriptorService } from '../AppDescriptorService/IAppDescriptorService';
import type { IPagesService } from '../PagesService/IPagesService';
import type { IReadyAppData } from '../ReadyAppData/IReadyAppData';
import { Services } from '../../ioc/services';
import { monitored } from '../Monitoring/monitored';
import type { IConsoleLogger } from '../ConsoleLogger/IConsoleLogger';

const token = 'token';

@injectable()
export class AddWidgetService implements IAddWidgetService {
  constructor(
    @inject(Services.EditorSDK)
    private editorSDK: EditorSDK,
    @inject(Services.AppDescriptorService)
    private appDescriptorService: IAppDescriptorService,
    @inject(Services.PagesService)
    private pagesService: IPagesService,
    @inject(Services.ReadyAppData)
    private readyAppDataService: IReadyAppData,
    @inject(Services.ConsoleLogger)
    private readonly consoleLogger: IConsoleLogger,
  ) {}

  private async reloadManifest() {
    await this.editorSDK.application.reloadManifest();
  }

  @monitored('add-widget-to-stage')
  public async addRequiredWidgetsToStage() {
    const appData = await this.getBlocksAppData();
    const settingsById = getInstallationSettingsByWidgetId(appData);
    // Needed so that widget nicknames are resolved via manifest before adding the widgets.
    await this.reloadManifest();

    await this.addWidgetToCurrentPage(settingsById);
    await this.addWidgetsAsNewPages(settingsById);
    await this.addWidgetsAsLightboxes(settingsById);
  }

  private async addWidgetToCurrentPage(settingsById: InstallationSettingsById) {
    const widgetIdToAdd = Object.keys(settingsById).find(
      (id) =>
        settingsById[id]?.installPage === InstallPage.CURRENT &&
        !settingsById[id]?.isUnified,
    );

    if (widgetIdToAdd) {
      await this.addMainWidgetPresetToStage(
        widgetIdToAdd,
        settingsById[widgetIdToAdd],
      );
    }
  }

  private async addMainWidgetPresetToStage(
    widgetPageId: string,
    settings: InstallationSettings | undefined,
    containerRef?: ComponentRef,
  ) {
    const { mainPreset } = await this.getPresets(widgetPageId, settings);

    if (!mainPreset) {
      return;
    }

    return this.addWidget({
      size: mainPreset.size,
      widgetPageId,
      presetId: mainPreset.id,
      containerRef,
      isStretched: settings?.isStretched,
    });
  }

  private async addWidgetsAsNewPages(settingsById: InstallationSettingsById) {
    const widgetIdsToAdd = Object.keys(settingsById).filter(
      (id) =>
        settingsById[id]?.installPage === InstallPage.PAGE &&
        !settingsById[id]?.isUnified,
    );

    for (const widgetId of widgetIdsToAdd) {
      await this.addWidgetAsPage(widgetId, settingsById[widgetId]);
    }
  }

  private async addWidgetAsPage(
    widgetPageId: string,
    settings: InstallationSettings | undefined,
  ) {
    const { editorType, appDefinitionId } = this.readyAppDataService;

    const isResponsive = editorType === EditorType.Responsive;
    const pageRef = await this.pagesService.addPage({
      title: settings?.pageOptions?.pageName || '',
      isResponsive,
      appDefinitionId,
      pageId: settings?.pageOptions?.pageId,
    });
    const sectionRef = await this.pagesService.findSectionInPage(
      pageRef,
      isResponsive,
    );

    const containerRef = sectionRef ?? pageRef;

    return this.addMainWidgetPresetToStage(
      widgetPageId,
      settings,
      containerRef,
    );
  }

  private async addWidgetsAsLightboxes(settingsById: InstallationSettingsById) {
    for (const [widgetId, widgetSettings] of Object.entries(settingsById)) {
      if (widgetSettings?.shouldAddAsLightbox) {
        await this.addWidgetAsALightbox(widgetId, widgetSettings);
      }
    }
  }

  private async addWidgetAsALightbox(
    widgetPageId: string,
    settings: InstallationSettings | undefined,
  ) {
    const { editorType, appDefinitionId } = this.readyAppDataService;
    const { lightboxId, lightboxName } = settings?.lightboxOptions || {};
    const isResponsive = editorType === EditorType.Responsive;
    const { mainPreset } = await this.getPresets(widgetPageId, settings);

    if (!mainPreset || !lightboxName) {
      throw new Error(
        'No main preset or lightbox name required to install Lightbox',
      );
    }

    const lightbox = await this.pagesService.createLightbox({
      title: lightboxName,
      isResponsive,
      appDefinitionId,
      lightboxId,
      size: mainPreset.size,
    });

    const popupContainer = isResponsive
      ? lightbox
      : await findFirstChild(this.editorSDK, lightbox);

    return this.addWidget({
      size: mainPreset.size,
      widgetPageId,
      presetId: mainPreset.id,
      containerRef: popupContainer,
    });
  }

  async getControllerName(widgetPageId: any, variationPageId: any) {
    const widgetData: WidgetDescriptor | undefined = _.get(
      (await this.appDescriptorService.get()).widgets,
      widgetPageId,
      undefined,
    );
    const variationData = _.find(widgetData?.variations, {
      rootCompId: `#${variationPageId}`,
    });
    return _.get(variationData, 'name') || widgetData?.name;
  }

  async addPreloader(widgetPageId: string, variationPageId?: string) {
    const pageRef = await this.editorSDK.pages.getCurrent('token');
    const controllerType = getUniqueControllerType(
      widgetPageId,
      this.readyAppDataService.appDefinitionId,
    );
    const controllerName = await this.getControllerName(
      widgetPageId,
      variationPageId,
    );
    const controllerData = createControllerData(
      this.readyAppDataService.appDefinitionId,
      controllerType,
      controllerName,
    );
    const initialWidgetDefinition =
      createAppWidgetInitialDefinition(controllerData);
    const initialControllerRef = await this.editorSDK.components.add(token, {
      componentDefinition: initialWidgetDefinition,
      pageRef,
    });
    // hack because of editorSDK waitForChangesApplied bug
    await this.editorSDK.document.components.getById(
      token,
      initialControllerRef,
    );
    return initialControllerRef;
  }

  @monitored('add-widget-closed')
  async addWidgetClosed({
    widgetId,
    presetId,
    size,
    position = { x: 0, y: 0 },
    containerRef,
    mobilePreset,
    isStretched,
  }: AddWidgetClosedOptions) {
    const mobileScopedPreset = mobilePreset && {
      layout: mobilePreset.id,
      style: mobilePreset.id,
    };

    const widgetRef =
      await this.editorSDK.application.appStudioWidgets.addWidget(token, {
        widgetId,
        installationType: 'closed',
        presetIds: presetId
          ? {
              layout: presetId,
              style: presetId,
            }
          : undefined,
        layout: {
          ...size,
          ...position,
          ...(isStretched
            ? {
                docked: {
                  left: { px: 0, vw: 0 },
                  right: { px: 0, vw: 0 },
                },
              }
            : {}),
        },
        layouts: {
          componentLayout: isStretched
            ? {
                type: 'ComponentLayout',
                height: { type: 'percentage', value: 100 },
                width: { type: 'percentage', value: 100 },
              }
            : {
                type: 'ComponentLayout',
                width: { type: 'px', value: size.width },
                height: { type: 'auto' },
                minWidth: { type: 'px', value: size.width },
                minHeight: { type: 'px', value: size.height },
              },
          containerLayout: {
            type: 'GridContainerLayout',
            rows: [{ type: 'fr', value: 1 }],
            columns: [{ type: 'fr', value: 1 }],
          },
          itemLayout: {
            id: '',
            type: 'GridItemLayout',
            gridArea: {
              columnStart: 1,
              columnEnd: 2,
              rowStart: 1,
              rowEnd: 2,
            },
            alignSelf: 'center',
            justifySelf: 'center',
            margins: {
              left: { type: 'percentage', value: 0 },
              top: { type: 'px', value: 0 },
            },
          },
        },
        containerRef,
        scopedPresets: {
          ...(mobileScopedPreset ? { mobile: mobileScopedPreset } : {}),
        },
        dimensionsByDevice: {
          desktop: {
            width: { type: 'px', value: size.width },
            height: { type: 'px', value: size.height },
          },
          ...(mobilePreset
            ? {
                mobile: {
                  width: { type: 'px', value: mobilePreset.size.width },
                  height: {
                    type: 'px',
                    value: mobilePreset.size.height,
                  },
                },
              }
            : {}),
        },
      });

    return widgetRef;
  }

  async addWidgetOpen(
    widgetId: string,
    widgetPageId: string,
    variationPageId?: string,
  ) {
    const preloaderRef = await this.addPreloader(widgetPageId, variationPageId);
    try {
      return this.editorSDK.application.appStudioWidgets.addWidget(token, {
        widgetId,
        variationId: variationPageId,
        installationType: 'open',
        layout: undefined as any,
      });
    } finally {
      await this.editorSDK.components.remove(token, {
        componentRef: preloaderRef,
      });
    }
  }

  async isResponsiveBlocksWidget(widgetId: string) {
    const appData = await this.getBlocksAppData();

    return (
      appData.widgets[widgetId]?.componentFields?.appStudioFields
        ?.blocksVersion === '2.0.0'
    );
  }

  private async getPresets(
    widgetPageId: string,
    settings: InstallationSettings | undefined,
  ) {
    const appDescriptor = await this.appDescriptorService.get();
    const widgetDescriptor = appDescriptor.widgets[widgetPageId];

    if (!widgetDescriptor) {
      this.consoleLogger.warn(
        'getPresets: widget was not found in app descriptor',
        { widgetPageId },
      );
      return {};
    }

    const presetById = widgetDescriptor.presets?.reduce<Record<string, Preset>>(
      (acc, preset) => {
        acc[removeHash(preset.presetId)] = preset;
        return acc;
      },
      {},
    );
    const getPreset = (presetId = '') => presetById?.[presetId];

    const desktopPreset = getPreset(settings?.mainPresets?.desktopPresetId);
    const mobilePreset = getPreset(settings?.mainPresets?.mobilePresetId);
    const firstPreset = widgetDescriptor.presets?.[0];
    const mainPreset = desktopPreset || firstPreset;

    return {
      mainPreset: {
        id: removeHash(mainPreset?.presetId),
        size: {
          width: mainPreset?.defaultSize?.width?.value || DEFAULT_WIDGET_WIDTH,
          height:
            mainPreset?.defaultSize?.height?.value || DEFAULT_WIDGET_HEIGHT,
        },
      },
      mobilePreset: mobilePreset
        ? {
            id: removeHash(mobilePreset.presetId),
            size: {
              width:
                mobilePreset.defaultSize?.width?.value || DEFAULT_WIDGET_WIDTH,
              height:
                mobilePreset.defaultSize?.height?.value ||
                DEFAULT_WIDGET_HEIGHT,
            },
          }
        : undefined,
    };
  }

  public async addWidget(options: AddWidgetOptions) {
    const {
      widgetPageId,
      position,
      size,
      presetId,
      containerRef,
      variationPageId,
      isStretched,
      shouldGetMobilePresetFromEditorPresence,
    } = options;

    const appData = await this.getBlocksAppData();
    const typeToDevCenterIdMap = buildTypeToDevCenterIdMap(appData);
    const widgetId = typeToDevCenterIdMap[widgetPageId];
    const settings = getInstallationSettings(widgetPageId, appData);

    let mobilePreset;
    if (shouldGetMobilePresetFromEditorPresence && presetId) {
      mobilePreset = await getMobilePresetFromEditorPresence(
        presetId,
        widgetPageId,
        appData,
        await this.appDescriptorService.get(),
      );
    } else {
      mobilePreset = (await this.getPresets(widgetPageId, settings))
        .mobilePreset;
    }

    const isResponsive = await this.isResponsiveBlocksWidget(widgetId);
    if (isResponsive) {
      return this.addWidgetClosed({
        widgetId,
        position,
        size,
        presetId,
        containerRef,
        mobilePreset,
        isStretched,
      });
    }
    return this.addWidgetOpen(widgetId, widgetPageId, variationPageId);
  }

  private getBlocksAppData() {
    return this.editorSDK.tpa.app.getDataByAppDefId(
      token,
      this.readyAppDataService.appDefinitionId,
    ) as Promise<ExtendedBlocksAppData>;
  }
}
